import React, { useState, useCallback } from 'react';
import { PageHeaderSection } from '../../components/common/PageHeadSection';
import { useTranslation } from 'react-i18next';
import { HotelSelect } from '../../components/common/HotelSelect';
import { useQuery } from 'react-query';
import { ParameterService } from '../../services/ParameterService';
import { mdlParameterListRequest } from '../../models/service-models/parameter/ParameterListRequest';
import { Skeleton, Space, Input } from 'antd';
import { ParameterTable } from '../../components/parameter/ParameterTable';
import { SFModal } from '../../components/elements/SFModal';
import { ParameterDetail } from '../../components/parameter/ParameterDetail';
import { mdlParameter } from '../../models/domain/Parameter';
import _ from 'lodash';
import { useAppSelector } from '../../store/Hooks';
import { mdlTablePagination } from '../../models/ui-models/TablePagination';

const Parameters = () => {
    const { t } = useTranslation();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlParameterListRequest>({ pageNumber: 1, pageSize: 10 });
    const [selectedParameter, setSelectedParameter] = useState<mdlParameter>();
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const language = useAppSelector(state => state.site.language)

    const debouncedSearch = useCallback(
        _.debounce((value: string) => setSearch(value), 500),
        []
    );

    const handleHotelSelect = useCallback((hotelId: string) => {
        setSelectedHotel(hotelId);
    }, []);

    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getParameters", selectedHotel, search, language, request],
        () => selectedHotel
            ? ParameterService.List({ ...request, hotelId: selectedHotel, name: search })
            : null,
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel,
        }
    );

    const handleEdit = (parameterRecord: mdlParameter) => {
        setSelectedParameter(parameterRecord);
        setOpenDetailModal(true);
    };

    const handleDetailCallback = () => {
        setOpenDetailModal(false);
        refetch();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };
    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }

    return (
        <>
            <div className="nk-content">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection pageTitle={t("parameters.title")} />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                <HotelSelect onChange={handleHotelSelect} />
                                                {selectedHotel && (
                                                    <>
                                                        <hr />
                                                        <Space align="center" style={{ justifyContent: "flex-end", display: "flex" }}>
                                                            <Input.Search
                                                                placeholder={t("parameters.parameterName")}
                                                                onChange={handleSearchChange}
                                                            />
                                                        </Space>
                                                        <hr />
                                                    </>
                                                )}
                                                {isLoading && <Skeleton active />}
                                                {!isLoading && data && (
                                                    <ParameterTable
                                                        onEdit={handleEdit}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) =>
                                                                `${t("pagination.total")} ${data?.body?.resultCount} ${t("pagination.result")}`,
                                                            locale: { items_per_page: `/ ${t("pagination.itemPerPage")}` }
                                                        }}
                                                        onTableParamsChange={handleTableParamsChange}
                                                        dataSource={data?.body?.data}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openDetailModal}
                toggler={() => setOpenDetailModal(!openDetailModal)}
                body={
                    <ParameterDetail
                        hotelId={selectedHotel}
                        hotelParameterId={selectedParameter?.id}
                        parameterId={selectedParameter?.parameterId}
                        callback={handleDetailCallback} />
                }
                title={t("parameters.detailTitle")}
            />
        </>
    );
};

export default Parameters;
